import { Material, Object3D, Scene } from "three";
import {
  OBJECT_COLLISION_HITS,
  type AppliedPushDebug,
  type CornerLineSnapDebug,
  type NarrowHit,
  type ObjectCollisionHitsDetail,
  type SeparationDebug,
} from "../core/drag/collision/ObjectCollisionHits";
import { Engine } from "../engine/Engine";
import { createVolumeDebugSegment, createVolumeDebugWire } from "./volumeDebugWire";
import {
  isDragCollisionDebugEnabled,
  volumeDebugColor,
  VOLUME_DEBUG_CHANGED_EVENT,
} from "../util/DebugFlags";

/** Uygulanmayan temaslar. Kırmızı kutu rengiyle karışmasın. */
const OTHER_EDGE = 0xb7c0cc;
/** O karede sürüklenene yazılan öteleme. */
const APPLIED_PUSH = 0x3dff6a;
/** Köşe hizası adayı (8 cm içinde). */
const CORNER_SNAP_OTHER = 0x7ec8ff;
/** Uygulanan köşe hizası. */
const CORNER_SNAP_CHOSEN = 0xffcc33;

let _group: Object3D | null = null;
let _lastHits: NarrowHit[] = [];
let _lastSeparations: SeparationDebug[] = [];
let _lastApplied: AppliedPushDebug[] = [];
let _lastCornerSnaps: CornerLineSnapDebug[] = [];
let _lastScene: Scene | undefined;
let _colorListener = false;

export function disposeObbDebugGroup(group: Object3D): void {
  group.traverse((obj) => {
    const mesh = obj as Object3D & {
      geometry?: { dispose: () => void };
      material?: Material | Material[];
    };
    if (!mesh.geometry?.dispose) return;
    mesh.geometry.dispose();
    const material = mesh.material;
    if (!material) return;
    if (Array.isArray(material)) material.forEach((entry) => entry.dispose());
    else material.dispose();
  });
  group.removeFromParent();
}

function dropCollisionGroup(): void {
  if (!_group) return;
  disposeObbDebugGroup(_group);
  _group = null;
}

export function clearDragCollisionDebugDraw(): void {
  _lastHits = [];
  _lastSeparations = [];
  _lastApplied = [];
  _lastCornerSnaps = [];
  _lastScene = undefined;
  dropCollisionGroup();
}

/** Tel kutu çizimi; `bindDragCollisionDebugToEngine` varsayılan dinleyicide kullanılır. */
export function syncDragCollisionDebugDraw(
  hits: NarrowHit[],
  scene: Scene | undefined,
  separations: SeparationDebug[] = [],
  appliedPushes: readonly AppliedPushDebug[] = [],
  cornerSnaps: readonly CornerLineSnapDebug[] = [],
): void {
  dropCollisionGroup();
  _lastHits = hits;
  _lastSeparations = separations;
  _lastApplied = appliedPushes.slice();
  _lastCornerSnaps = cornerSnaps.slice();
  _lastScene = scene;
  if (
    !scene ||
    (hits.length === 0 &&
      separations.length === 0 &&
      appliedPushes.length === 0 &&
      cornerSnaps.length === 0)
  ) {
    return;
  }

  const group = new Object3D();
  group.name = "collisionDebugGroup";
  const contact = volumeDebugColor("contact");
  for (const h of hits) {
    group.add(createVolumeDebugWire(h.selfObb.clone(), contact));
    group.add(createVolumeDebugWire(h.obstacle.obb.clone(), contact));
  }
  for (const separation of separations) {
    group.add(createVolumeDebugSegment(separation.from, separation.to, OTHER_EDGE));
  }
  for (const appliedPush of appliedPushes) {
    group.add(
      createVolumeDebugSegment(appliedPush.from, appliedPush.to, APPLIED_PUSH),
    );
  }
  for (const snap of cornerSnaps) {
    group.add(
      createVolumeDebugSegment(
        snap.from,
        snap.to,
        snap.chosen ? CORNER_SNAP_CHOSEN : CORNER_SNAP_OTHER,
      ),
    );
  }
  if (group.children.length === 0) return;

  scene.add(group);
  _group = group;
}

function onObjectCollisionHitsEvent(e: Event): void {
  const detail = (e as CustomEvent<ObjectCollisionHitsDetail>).detail;
  const hasDebug =
    !!detail &&
    (detail.hits.length > 0 ||
      (detail.separations?.length ?? 0) > 0 ||
      (detail.cornerSnaps?.length ?? 0) > 0);
  if (!detail || !isDragCollisionDebugEnabled() || !hasDebug) {
    clearDragCollisionDebugDraw();
    return;
  }
  syncDragCollisionDebugDraw(
    detail.hits,
    detail.scene,
    detail.separations ?? [],
    detail.appliedPushes ?? (detail.appliedPush ? [detail.appliedPush] : []),
    detail.cornerSnaps ?? [],
  );
}

function onVolumeDebugChanged(): void {
  if (
    !isDragCollisionDebugEnabled() ||
    !_lastScene ||
    (_lastHits.length === 0 &&
      _lastSeparations.length === 0 &&
      _lastApplied.length === 0 &&
      _lastCornerSnaps.length === 0)
  ) {
    return;
  }
  syncDragCollisionDebugDraw(
    _lastHits,
    _lastScene,
    _lastSeparations,
    _lastApplied,
    _lastCornerSnaps,
  );
}

/** Room designer: `?debugCollision=1` iken tel kutu. Kendi dinleyicini eklemek için önce bunu çağırma. */
export function bindDragCollisionDebugToEngine(engine: Engine): void {
  engine.addEventListener(OBJECT_COLLISION_HITS, onObjectCollisionHitsEvent);
  if (_colorListener) return;
  _colorListener = true;
  window.addEventListener(VOLUME_DEBUG_CHANGED_EVENT, onVolumeDebugChanged);
}
